import { useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, Text, View } from "react-native";
import {
  AlertCircle,
  Activity,
  Beaker,
  BookOpen,
  CalendarDays,
  CheckCircle,
  ClipboardCheck,
  DollarSign,
  FileText,
  Heart,
  History,
  Info,
  Moon,
  Plus,
  TestTube2,
  Timer,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { CommonActions } from "@react-navigation/native";
import {
  getCollectionLabel,
  getBlocksByKind,
  getDataPointForMarkerName,
  getDataPointsForTest,
  getTestsForDataPoint,
  makeId,
  type ContentBlock,
  type TestBody,
} from "@src/packages/content";
import { useLocale } from "@src/packages/i18n/useLocale";
import { useTranslations } from "@src/packages/i18n";
import {
  getBiomarkerIdentity,
  groupCanonicalResults,
  type CanonicalTestResult,
  useCanonicalTestResults,
} from "@src/packages/test-results";
import {
  useDeleteTestResult,
  useUpdateTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import {
  getBiomarkerFactsheet as getResultBiomarkerFactsheet,
  type BiomarkerFactsheet,
} from "@src/packages/test-results/content/biomarker-factsheets";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import {
  BrownButton,
  IconButton,
  SectionTitle,
  SectionTitleRow,
} from "@louise/moon-ui";
import FactSheetHeader, {
  FactSheetBadge,
  type FactSheetHeaderBadge,
} from "../../components/FactSheetHeader";
import AccessGridCard, {
  AccessGridCardPill,
} from "../../components/access/AccessGridCard";
import FactSheetTabControl, {
  type FactSheetTab,
} from "../../components/FactSheetTabControl";
import EvidenceLevelModal from "../../components/EvidenceLevelModal";
import EvidenceSourceRows from "../../components/EvidenceSourceRows";
import TestResultActionsCard from "../../components/TestResultActionsCard";
import { HistoryChart } from "../../components/HistoryChart";
import {
  DocumentTestResultRow,
  DocumentRow,
  documentTestTubesIcon,
  type DocumentTestResultCategory,
} from "../documents/DocumentRow";
import type {
  LabResult,
  LabResultHistoryPoint,
} from "../care/sections/ResultsLabsCard";
import TestResultDetailDialog from "../../components/TestResultDetailDialog";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import testTubesIllustration from "../../../assets/images/you-icons/test-tubes.png";
import bloodDropIllustration from "../../../assets/images/you-icons/blood.png";
import {
  DEV_PREVIEW_AMH_RESULT,
  isDevPreviewLabResultsEnabled,
} from "../../dev/labResultsFixture";

type Props =
  | NativeStackScreenProps<AccessStackParamList, "AmhComparison">
  | NativeStackScreenProps<AccessStackParamList, "TestComparison">;
type PageTab = "overview" | "values" | "biology" | "testing";
type TestQuickFact = NonNullable<TestBody["quickFacts"]>[number];
type ResultPoint = LabResultHistoryPoint;

const QUICK_FACT_ICONS: Record<TestQuickFact["kind"], LucideIcon> = {
  sample: Beaker,
  time: Timer,
  pain: Activity,
  anesthesia: Moon,
  prep: ClipboardCheck,
  results: FileText,
};

const QUICK_FACT_LABEL_KEYS: Record<TestQuickFact["kind"], string> = {
  sample: "learn.test_detail.quick_fact_sample",
  time: "learn.test_detail.quick_fact_time",
  pain: "learn.test_detail.quick_fact_pain",
  anesthesia: "learn.test_detail.quick_fact_anesthesia",
  prep: "learn.test_detail.quick_fact_prep",
  results: "learn.test_detail.quick_fact_results",
};

function getTestBlock(id: string): ContentBlock<"test"> | null {
  const candidateIds = id.startsWith("test-") ? [id] : [id, `test-${id}`];
  return (
    getBlocksByKind("test").find((block) =>
      candidateIds.some((candidateId) => block.id === makeId(candidateId)),
    ) ?? null
  );
}

function buildLabResult(
  results: CanonicalTestResult[],
  factsheet: BiomarkerFactsheet,
  markerName: string,
  formatDate: (date: string) => string,
): LabResult | null {
  const canonicalHistory = groupCanonicalResults(results)[0];
  if (!canonicalHistory) return null;
  const latest = canonicalHistory.latest;

  return {
    id: latest.id,
    dataPointId: canonicalHistory.dataPointId,
    name: latest.name ?? markerName,
    value: latest.value,
    unit: latest.unit ?? "",
    date: latest.date ? formatDate(latest.date) : "",
    cycleId: latest.cycleId,
    trend: canonicalHistory.trend,
    clinicalAssessment: latest.clinicalAssessment,
    attachment: latest.source.attachment,
    provenance: latest.source,
    isAbnormal:
      latest.clinicalAssessment === "low" ||
      latest.clinicalAssessment === "high",
    history: canonicalHistory.results.map((result) => ({
      id: result.id,
      date: result.date ? formatDate(result.date) : "",
      value: result.value,
      clinicalAssessment: result.clinicalAssessment,
      clinicalAssessmentDetails: result.clinicalAssessmentDetails,
      attachment: result.source.attachment,
      provenance: result.source,
    })),
    clinicalAssessmentDetails: latest.clinicalAssessmentDetails,
    ...factsheet,
  };
}

function InfoBlock({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: string;
}) {
  return (
    <View style={{ gap: 8 }}>
      <SectionTitle icon={Icon}>{title}</SectionTitle>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: "#6E625B",
          lineHeight: 21,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

function QuickFacts({
  items,
  t,
}: {
  items: NonNullable<TestBody["quickFacts"]>;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
      {items.map((item, index) => {
        const Icon = QUICK_FACT_ICONS[item.kind];
        return (
          <View
            key={`${item.kind}-${index}`}
            style={{
              flexBasis: "47%",
              flexGrow: 1,
              minWidth: 140,
              minHeight: 78,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.3)",
              paddingHorizontal: 12,
              paddingVertical: 11,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 7,
                marginBottom: 7,
              }}
            >
              <View
                style={{
                  width: 23,
                  height: 23,
                  borderRadius: 12,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(88,78,68,0.1)",
                }}
              >
                <Icon size={13} color="#6E625B" strokeWidth={1.8} />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 10,
                  fontWeight: "700",
                  color: "#9A8E85",
                  textTransform: "uppercase",
                }}
              >
                {item.label ?? t(QUICK_FACT_LABEL_KEYS[item.kind])}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: "#6E625B",
                lineHeight: 18,
              }}
            >
              {item.value}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

function MarkerTiming({
  items,
}: {
  items: NonNullable<TestBody["markerTiming"]>;
}) {
  return (
    <View style={{ gap: 10 }}>
      {items.map((item, index) => (
        <View
          key={`${item.marker}-${index}`}
          style={{
            flexDirection: "row",
            gap: 12,
            padding: 12,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.3)",
          }}
        >
          <CalendarDays size={17} color="#6E625B" strokeWidth={1.7} />
          <View style={{ flex: 1, gap: 4 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, fontWeight: "700", color: "#6E625B" }}
            >
              {item.marker}
            </Text>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 13, fontWeight: "600", color: "#7D6F66" }}
            >
              {item.timing}
            </Text>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, lineHeight: 17, color: "#9A8E85" }}
            >
              {item.reason}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

function StepList({ items }: { items: string[] }) {
  return (
    <View
      style={{
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        backgroundColor: "rgba(255,255,255,0.3)",
        overflow: "hidden",
      }}
    >
      {items.map((item, index) => (
        <View
          key={`${item.slice(0, 20)}-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            padding: 12,
            borderTopWidth: index === 0 ? 0 : 1,
            borderTopColor: "rgba(0,0,0,0.05)",
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(88,78,68,0.1)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, fontWeight: "700", color: "#6E625B" }}
            >
              {index + 1}
            </Text>
          </View>
          <Text
            className="font-dm-sans"
            style={{ flex: 1, fontSize: 13, lineHeight: 19, color: "#7D6F66" }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function BenefitList({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 10 }}>
      {items.map((item, index) => (
        <View
          key={`${item.slice(0, 20)}-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(86,124,84,0.10)",
              marginTop: 1,
            }}
          >
            <CheckCircle size={13} color="#567C54" strokeWidth={2} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: "#7D6F66",
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function TrackedDataPoints({
  dataPoints,
  t,
}: {
  dataPoints: ContentBlock<"data-point">[];
  t: ReturnType<typeof useTranslations>;
}) {
  if (dataPoints.length < 2) return null;

  return (
    <View style={{ gap: 10 }}>
      <SectionTitle icon={Beaker}>
        {t("access.testing.comparison.markers_tracked")}
      </SectionTitle>
      {dataPoints.map((dataPoint) => {
        const body = dataPoint.body;
        return (
          <View
            key={dataPoint.id}
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 12,
              padding: 12,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.28)",
            }}
          >
            <View
              style={{
                width: 34,
                height: 34,
                borderRadius: 17,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.045)",
              }}
            >
              <Beaker size={16} color="#6E625B" />
            </View>
            <View style={{ flex: 1, gap: 6 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: "700",
                  color: "#6E625B",
                  lineHeight: 18,
                }}
              >
                {dataPoint.title}
              </Text>
              {body.interpretation ? (
                <Text
                  className="font-dm-sans"
                  numberOfLines={3}
                  style={{ fontSize: 12, color: "#9A8E85", lineHeight: 17 }}
                >
                  {body.interpretation}
                </Text>
              ) : null}
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                {body.sampleTypes?.map((sampleType) => (
                  <FactSheetBadge key={sampleType} label={sampleType} />
                ))}
                {body.unit ? <FactSheetBadge label={body.unit} /> : null}
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function getResultCategory(result: LabResult): DocumentTestResultCategory {
  switch (result.clinicalAssessment) {
    case "low":
      return "low";
    case "high":
      return "high";
    case "normal":
      return "normal";
    default:
      if (result.isAbnormal) return "borderline";
      if (result.trend === "up") return "high";
      if (result.trend === "down") return "low";
      return "normal";
  }
}

function CanonicalValues({
  result,
  marker,
  resultSubtitle,
  onOpenDetails,
  onViewAll,
  t,
}: {
  result: LabResult | null;
  marker: string;
  resultSubtitle: string;
  onOpenDetails: (point: ResultPoint) => void;
  onViewAll: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  if (!result) {
    return (
      <View style={{ marginTop: 18 }}>
        <View
          style={{
            paddingVertical: 20,
            paddingHorizontal: 16,
            backgroundColor: "rgba(0,0,0,0.02)",
            borderRadius: 12,
            alignItems: "center",
            gap: 6,
          }}
        >
          <History size={20} color="#9A8E85" strokeWidth={1.5} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              fontWeight: "500",
              color: "#6E625B",
              textAlign: "center",
            }}
          >
            {t("care.lab_result.no_values_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: "#9A8E85",
              textAlign: "center",
              lineHeight: 19,
            }}
          >
            {t("access.testing.comparison.no_result_body", {
              params: { marker },
            })}
          </Text>
          <BrownButton
            label={t("access.testing.comparison.add_result")}
            size="compact"
            variant="outline"
            fullWidth={false}
            onPress={onViewAll}
          />
        </View>
      </View>
    );
  }

  const valuePoints: ResultPoint[] =
    result.history && result.history.length > 0
      ? result.history
      : [{ id: result.id, date: result.date, value: result.value }];
  const latestFirstPoints = [...valuePoints].reverse();
  const category = getResultCategory(result);

  return (
    <View>
      <HistoryChart
        history={valuePoints}
        unit={result.unit}
        isAbnormal={result.isAbnormal}
      />

      <View style={{ marginTop: 18 }}>
        <DocumentRow
          title={t("care.lab_result.add_value")}
          subtitle={t("care.lab_result.add_value_body", {
            params: { name: result.name },
          })}
          imageSource={documentTestTubesIcon}
          trailing={<Plus size={18} color="#9A8E85" strokeWidth={1.5} />}
          onPress={onViewAll}
          showChevron={false}
        />
        <View style={{ marginTop: 9, gap: 9 }}>
          {latestFirstPoints.map((point) => (
            <DocumentTestResultRow
              key={point.id}
              title={result.name}
              subtitle={resultSubtitle}
              dateLabel={point.date}
              value={point.value}
              unit={result.unit}
              category={category}
              onPress={() => onOpenDetails(point)}
            />
          ))}
        </View>
      </View>
    </View>
  );
}

function OverviewResultState({
  result,
  marker,
  resultSubtitle,
  onOpenResult,
  onUploadResult,
  onGetTested,
  t,
}: {
  result: LabResult | null;
  marker: string;
  resultSubtitle: string;
  onOpenResult: (point: ResultPoint) => void;
  onUploadResult: () => void;
  onGetTested: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  if (result) {
    return (
      <DocumentTestResultRow
        title={result.name}
        subtitle={resultSubtitle}
        dateLabel={result.date}
        value={result.value}
        unit={result.unit}
        category={getResultCategory(result)}
        onPress={() =>
          onOpenResult({
            id: result.id,
            date: result.date,
            value: result.value,
            attachment: result.attachment,
          })
        }
      />
    );
  }

  return (
    <TestResultActionsCard
      addResultDescription={t(
        "access.testing.comparison.test_not_logged_description",
        { params: { marker } },
      )}
      getTestedDescription={t(
        "access.testing.comparison.get_tested_description",
      )}
      onAddResult={onUploadResult}
      onGetTested={onGetTested}
      t={t}
    />
  );
}

function TestOptionCard({
  test,
  onPress,
  imageSource,
}: {
  test: ContentBlock<"test">;
  onPress: () => void;
  imageSource: typeof testTubesIllustration;
}) {
  const body = test.body;
  const collectionLabel = getCollectionLabel(body);
  const cost = body.costRange
    ? `$${body.costRange.low}–$${body.costRange.high}`
    : undefined;

  return (
    <AccessGridCard
      title={test.title}
      description={test.subtitle}
      imageSource={imageSource}
      imageStyle={{ width: 66, height: 84 }}
      onPress={onPress}
      tags={
        <>
          <AccessGridCardPill label={collectionLabel} icon={TestTube2} />
          {body.turnaroundTime ? (
            <AccessGridCardPill label={body.turnaroundTime} icon={Timer} />
          ) : null}
          {cost ? <AccessGridCardPill label={cost} icon={DollarSign} /> : null}
        </>
      }
    />
  );
}

export default function AmhComparisonScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { abbrev } = useDateFormat();
  const handleBack = useSmartBack({
    returnTab:
      route.name === "TestComparison" ? route.params.returnTab : undefined,
    fallbackTab: "Access",
    fallbackScreen: "Testing",
  });
  const testResultsQuery = useCanonicalTestResults();
  const updateTestResult = useUpdateTestResult();
  const deleteTestResult = useDeleteTestResult();
  const devPreviewLabResults = isDevPreviewLabResultsEnabled();
  const [activeTab, setActiveTab] = useState<PageTab>("overview");
  const [selectedValue, setSelectedValue] = useState<ResultPoint | null>(null);
  const [evidenceLevel, setEvidenceLevel] = useState<string | null>(null);

  const comparisonTestId =
    route.name === "AmhComparison" ? "test-amh" : route.params.testId;
  const test = getTestBlock(comparisonTestId);
  const trackedDataPoints = test ? getDataPointsForTest(test.id) : [];
  const primaryDataPoint = trackedDataPoints[0];
  const markerName = (
    primaryDataPoint?.title ??
    test?.body.testName ??
    test?.title ??
    "Test"
  ).replace(/\s+level$/i, "");
  const factsheet = useMemo(
    () => getResultBiomarkerFactsheet(markerName, locale),
    [locale, markerName],
  );
  const linkedTests = useMemo(
    () => (primaryDataPoint ? getTestsForDataPoint(primaryDataPoint.id) : []),
    [primaryDataPoint],
  );
  const markerResults = useMemo(() => {
    const identity = getBiomarkerIdentity(markerName);
    return [...(testResultsQuery.data ?? [])].filter(
      (result) => getBiomarkerIdentity(result.name) === identity,
    );
  }, [markerName, testResultsQuery.data]);
  const isAmh = test?.id === makeId("test-amh");
  const labResult = useMemo(
    () =>
      isAmh && devPreviewLabResults
        ? DEV_PREVIEW_AMH_RESULT
        : factsheet
          ? buildLabResult(markerResults, factsheet, markerName, abbrev)
          : null,
    [abbrev, devPreviewLabResults, factsheet, isAmh, markerName, markerResults],
  );
  const openResultDialog = (point: ResultPoint) => {
    setSelectedValue(point);
  };
  const handleEditResult = async (
    id: string,
    data: { patientValue?: string; unit?: string; date?: string },
  ) => {
    await updateTestResult.mutateAsync({ testResultId: id, data });
    setSelectedValue(null);
  };
  const handleDeleteResult = async (id: string) => {
    await deleteTestResult.mutateAsync(id);
    setSelectedValue(null);
  };

  if (!test || !factsheet) return null;
  const testBody = test.body;
  const testOptions = [
    test,
    ...linkedTests.filter((linkedTest) => linkedTest.id !== test.id),
  ];
  const includedTests = testOptions.filter(
    (linkedTest) => linkedTest.id !== test.id,
  );
  const resultSubtitle = isAmh
    ? t("all_lab_results.purpose_amh")
    : factsheet.description;
  const biologyText = factsheet.biology;
  const measurementText = factsheet.measurementDetails ?? factsheet.howItWorks;
  const variationText = factsheet.biologicalVariation;
  const howItWorksSteps =
    testBody.howItWorksSteps ??
    testBody.howItWorks
      .split(/(?<=[.!?])\s+/)
      .map((step) => step.trim())
      .filter(Boolean);
  const tabs: FactSheetTab<PageTab>[] = [
    {
      value: "overview",
      label: t("care.lab_result.tab_overview"),
      shortLabel: t("care.lab_result.tab_overview_short"),
      icon: Info,
    },
    {
      value: "values",
      label: t("care.lab_result.tab_values"),
      shortLabel: t("care.lab_result.tab_values_short"),
      icon: History,
    },
    {
      value: "biology",
      label: t("care.lab_result.tab_biology"),
      shortLabel: t("care.lab_result.tab_biology_short"),
      icon: Beaker,
    },
    {
      value: "testing",
      label: t("access.testing.comparison.get_tested"),
      shortLabel: t("access.testing.comparison.get_tested_short"),
      icon: TestTube2,
    },
  ];
  const badges: FactSheetHeaderBadge[] = [
    { label: t("access.testing.comparison.blood_test"), icon: Beaker },
    ...(testBody.atHome &&
    !testBody.clinicRequired &&
    /lab/i.test(test.subtitle ?? "")
      ? [{ label: t("access.testing.comparison.also_available_at_home") }]
      : []),
    ...(testBody.clinicRequired
      ? [{ label: t("learn.test_detail.badge_clinic_visit") }]
      : []),
  ];

  const openAllResults = () => {
    navigation.getParent()?.dispatch(
      CommonActions.navigate({
        name: "Care",
        params: { screen: "AllLabResults" },
      }),
    );
  };

  const openRelatedMarker = (marker: string) => {
    const dataPoint = getDataPointForMarkerName(marker);
    if (!dataPoint) return;

    const markerQuery = marker.toLowerCase();
    const linkedTests = getTestsForDataPoint(dataPoint.id);
    const markerTests = linkedTests.filter(
      (linkedTest) => linkedTest.id !== test.id,
    );
    const linkedTest =
      markerTests.find((linkedTest) =>
        [linkedTest.title, linkedTest.body.testName].some((value) =>
          value?.toLowerCase().includes(markerQuery),
        ),
      ) ??
      markerTests.find((linkedTest) =>
        linkedTest.subtitle?.toLowerCase().includes(markerQuery),
      ) ??
      markerTests[0] ??
      linkedTests[0];

    if (linkedTest) {
      const isSingleMarkerTest =
        getDataPointsForTest(linkedTest.id).length === 1;
      navigation.navigate(
        isSingleMarkerTest ? "TestComparison" : "TestDetail",
        { testId: String(linkedTest.id) },
      );
    }
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            ...GLASS_CARD_STYLE,
            paddingHorizontal: 24,
            paddingTop: 24,
            paddingBottom: 32,
          }}
        >
          <View
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2,
            }}
          >
            <IconButton
              onPress={handleBack}
              hitSlop={8}
              size={32}
              surface="soft"
              backgroundColor="rgba(0,0,0,0.05)"
              pressedBackgroundColor="rgba(0,0,0,0.05)"
              pressedOpacity={1}
              accessibilityLabel={t(
                "learn.article_actions.close_accessibility",
              )}
            >
              <X size={16} color="#9A8E85" />
            </IconButton>
          </View>

          <FactSheetHeader
            title={markerName}
            subtitle={
              isAmh
                ? t("access.testing.comparison.amh_subtitle")
                : factsheet.description
            }
            imageSource={testTubesIllustration}
            imageSize={86}
            badges={badges}
          />

          <FactSheetTabControl
            active={activeTab}
            onChange={setActiveTab}
            tabs={tabs}
          />

          {activeTab === "overview" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <OverviewResultState
                result={labResult}
                marker={markerName}
                resultSubtitle={resultSubtitle}
                onOpenResult={openResultDialog}
                onUploadResult={openAllResults}
                onGetTested={() => setActiveTab("testing")}
                t={t}
              />

              <InfoBlock
                icon={Heart}
                title={t("learn.test_detail.section_what_it_measures")}
              >
                {testBody.whatItMeasures}
              </InfoBlock>

              <View>
                <SectionTitle icon={Info}>
                  {t("access.testing.comparison.at_a_glance")}
                </SectionTitle>
                {testBody.quickFacts?.length ? (
                  <View style={{ marginTop: 10 }}>
                    <QuickFacts items={testBody.quickFacts} t={t} />
                  </View>
                ) : null}
              </View>

              {trackedDataPoints.length > 1 ? (
                <TrackedDataPoints dataPoints={trackedDataPoints} t={t} />
              ) : null}

              {testBody.howItWorks ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Wrench}>
                    {t("learn.test_detail.section_how_it_works")}
                  </SectionTitle>
                  <StepList items={howItWorksSteps} />
                </View>
              ) : null}

              {testBody.whyItMatters?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Heart}>
                    {t("learn.test_detail.section_why_it_matters")}
                  </SectionTitle>
                  <BenefitList items={testBody.whyItMatters} />
                </View>
              ) : null}

              <View style={{ gap: 10 }}>
                <SectionTitle icon={Beaker}>
                  {t("care.lab_result.typical_range")}
                </SectionTitle>
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 14, lineHeight: 21, color: "#6E625B" }}
                >
                  {factsheet.normalRange}
                </Text>
                {factsheet.normalRangeBullets?.map((bullet) => (
                  <View
                    key={bullet}
                    style={{ flexDirection: "row", gap: 8, paddingLeft: 2 }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{ fontSize: 14, color: "#9A8E85" }}
                    >
                      •
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        flex: 1,
                        fontSize: 13,
                        lineHeight: 19,
                        color: "#7D6F66",
                      }}
                    >
                      {bullet}
                    </Text>
                  </View>
                ))}
              </View>

              {factsheet.relatedMarkers?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Beaker}>
                    {t("access.testing.comparison.connected_markers")}
                  </SectionTitle>
                  <View
                    style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}
                  >
                    {factsheet.relatedMarkers.map((marker) => (
                      <FactSheetBadge
                        key={marker}
                        label={marker}
                        onPress={() => openRelatedMarker(marker)}
                      />
                    ))}
                  </View>
                  {includedTests.length ? (
                    <>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          lineHeight: 19,
                          color: "#9A8E85",
                        }}
                      >
                        {t("access.testing.comparison.included_in")}
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: 7,
                        }}
                      >
                        {includedTests.map((includedTest) => (
                          <FactSheetBadge
                            key={includedTest.id}
                            label={includedTest.title}
                            onPress={() =>
                              navigation.navigate("TestDetail", {
                                testId: String(includedTest.id),
                              })
                            }
                          />
                        ))}
                      </View>
                    </>
                  ) : null}
                </View>
              ) : null}

              {test.sources?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("learn.test_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={test.sources}
                    onEvidenceLevelPress={setEvidenceLevel}
                  />
                </View>
              ) : null}

              {testBody.considerations?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertCircle}>
                    {t("learn.test_detail.section_considerations")}
                  </SectionTitle>
                  {testBody.considerations.map((item) => (
                    <Text
                      key={item}
                      className="font-dm-sans"
                      style={{ fontSize: 13, lineHeight: 19, color: "#7D6F66" }}
                    >
                      • {item}
                    </Text>
                  ))}
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "values" ? (
            <View style={{ marginTop: 24 }}>
              <SectionTitleRow
                title={t("access.testing.comparison.your_results")}
                icon={History}
                action={{
                  label: t("all_lab_results.title_all"),
                  onPress: openAllResults,
                }}
              />
              {testResultsQuery.isLoading ? (
                <ActivityIndicator color="#6E625B" />
              ) : (
                <CanonicalValues
                  result={labResult}
                  marker={markerName}
                  resultSubtitle={resultSubtitle}
                  onOpenDetails={openResultDialog}
                  onViewAll={openAllResults}
                  t={t}
                />
              )}
            </View>
          ) : null}

          {activeTab === "biology" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              {biologyText ? (
                <InfoBlock
                  icon={Beaker}
                  title={t("care.lab_result.biological_basis")}
                >
                  {biologyText}
                </InfoBlock>
              ) : null}
              {measurementText ? (
                <InfoBlock
                  icon={Wrench}
                  title={t("care.lab_result.how_it_is_measured")}
                >
                  {measurementText}
                </InfoBlock>
              ) : null}
              {variationText ? (
                <InfoBlock
                  icon={History}
                  title={t("care.lab_result.how_it_changes_over_time")}
                >
                  {variationText}
                </InfoBlock>
              ) : null}
              {factsheet.testingTiming ? (
                <InfoBlock
                  icon={CalendarDays}
                  title={t("care.lab_result.when_it_is_measured")}
                >
                  {factsheet.testingTiming}
                </InfoBlock>
              ) : null}
              {testBody.markerTiming?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={CalendarDays}>
                    {t("learn.test_detail.section_marker_timing")}
                  </SectionTitle>
                  <MarkerTiming items={testBody.markerTiming} />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "testing" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
                {testOptions.map((option, index) => (
                  <View key={option.id} style={{ width: "48%" }}>
                    <TestOptionCard
                      test={option}
                      imageSource={
                        index === 0
                          ? testTubesIllustration
                          : bloodDropIllustration
                      }
                      onPress={() =>
                        navigation.navigate("TestDetail", {
                          testId: String(option.id),
                        })
                      }
                    />
                  </View>
                ))}
              </View>
            </View>
          ) : null}

          <View
            style={{
              marginTop: 32,
              padding: 14,
              borderRadius: 12,
              backgroundColor: "rgba(0,0,0,0.03)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: "#9A8E85",
                lineHeight: 17,
                textAlign: "center",
              }}
            >
              {t("learn.disclaimer_medical_info")}
            </Text>
          </View>
        </View>
      </ScrollView>

      <TestResultDetailDialog
        result={labResult}
        point={selectedValue}
        onClose={() => setSelectedValue(null)}
        onAttachSource={async (id, attachmentId) => {
          await updateTestResult.mutateAsync({
            testResultId: id,
            data: { attachmentId },
          });
        }}
        onEdit={handleEditResult}
        onDelete={handleDeleteResult}
      />
      <EvidenceLevelModal
        level={evidenceLevel}
        visible={evidenceLevel !== null}
        onClose={() => setEvidenceLevel(null)}
      />
    </GlassScreenLayout>
  );
}
